import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import LoaderComponent from '@components/LoaderComponent';

import useDocumentTitle from '@hooks/useDocumentTitle';

import TopHeader from '@features/design/components/TopHeader';
import DesignToolRightSideBar from '@features/designTool/components/DesignToolRightSideBar';
import Presentation from '@features/designTool/components/Presentation';
import Measurement from '@features/measurement/Measurement';
import MaterialsV2 from '@features/design/components/MaterialsV2';

import Techpack from '@pages/designTool/Techpack';
import DesignTool from '@pages/designTool/DesignTool';
import DesignView from '@pages/design/DesignView';

import { clearCurrentActivePage, setCurrentActivePage } from '@store/actions/ActivePageAction';
import { clearAllChats, storeEntityIdForListPage, storeNewMsg } from '@store/actions/ChatAction';
import { clearDesignTool, getMemberList } from '@store/actions/DesignToolAction';
import { getDocumentResponse, useDesignViewModal } from '@store/reducers';

import conversationService from '@services/conversation-service';

import { ACCESS_TOKEN, getStompCLient } from '@utils/Util';

import CostingAgent from '@/features/costing-agent/CostingAgent';

let stompClient = getStompCLient();
stompClient.reconnectDelay = 1000;

const DesignEdit = () => {
	const [activeTab, setActiveTab] = useState('design');
	const [openCommentPanel, setOpenCommentPanel] = useState(false);
	const [selectedPost, setSelectedPost] = useState(null);
	const [loader, setLoader] = useState(true);
	const { howToMeasureDocResponse } = useSelector(getDocumentResponse);
	const designToolStore = useSelector((store) => store.designToolStore);
	const activePage = useSelector((store) => store.activePageReducer.currentPage);
	const isGlobalLoading = useSelector((store) => store.loaderStore);
	const designViewInfo = useDesignViewModal();
	const { id } = useParams();
	const location = useLocation();
	const dispatch = useDispatch();
	const measurementRef = useRef(null);
	const [isDesignSaved, setIsDesignSaved] = useState(true);

	useDocumentTitle(
		`${designToolStore.referenceNumber ? designToolStore.referenceNumber : ''}: Design edit`
	);

	const setActivePageDetails = (pageName, id = null) => {
		if (pageName === 'designs') {
			dispatch(
				setCurrentActivePage({
					id: id,
					type: 'PRODUCT_CONVERSATION',
					pageType: 'DETAILS'
				})
			);
		}
	};

	useEffect(() => {
		let activeTabName = new URLSearchParams(location.search).get('activeTab');
		if (activeTabName) {
			setActiveTab(activeTabName);
		}
		setActivePageDetails(location.pathname.split('/')[1], id);
	}, [location]);

	useEffect(() => {
		let isShowComment = new URLSearchParams(location.search).get('showComment');
		let postId = new URLSearchParams(location.search).get('postId');
		if (isShowComment) {
			setOpenCommentPanel(true);
			setSelectedPost(parseInt(postId));
		}
		return () => {
			dispatch(clearCurrentActivePage());
			stompClient.disconnect();
		};
	}, []);

	const onConnected = async () => {
		const channel = await conversationService.getRequestChannel(stompClient);
		stompClient.subscribe(
			channel,
			(payload) => {
				const postResponse = JSON.parse(payload.body);
				if (
					postResponse?.content?.entityId === parseInt(activePage.id) &&
					postResponse?.content?.conversationTopic === activePage.type &&
					activePage.pageType === 'DETAILS'
				) {
					dispatch(storeNewMsg(postResponse?.content));
				}
				if (
					postResponse?.content?.conversationTopic === activePage.type &&
					activePage.pageType === 'LIST'
				) {
					dispatch(storeEntityIdForListPage(postResponse?.content?.entityId));
				}
			},
			{ jwt: ACCESS_TOKEN() }
		);
	};

	const onError = (error) => {
		if (error?.headers?.message === '401') {
			stompClient.disconnect(function () {});
		}
	};

	const registerConnection = async () => {
		stompClient.connect({ jwt: ACCESS_TOKEN() }, onConnected, onError);
	};

	useEffect(() => {
		let htmlElement = document.getElementsByTagName('html');
		if (htmlElement && htmlElement.length > 0) {
			htmlElement[0].style.zoom = 'unset';
			htmlElement[0].style.backgroundColor = '#eaeaea';
		}
		dispatch(getMemberList(id));

		return () => {
			if (htmlElement && htmlElement.length > 0) {
				htmlElement[0].removeAttribute('style');
			}
			localStorage.removeItem('fromRoute');
		};
	}, []);

	useEffect(() => {
		registerConnection();
		return () => {
			dispatch(clearAllChats());
		};
	}, [activePage]);

	useEffect(() => {
		return () => {
			dispatch(clearDesignTool());
		};
	}, []);

	const onChangeActiveTab = (tab) => {
		setActiveTab(tab);
	};

	return (
		<LoaderComponent loading={loader || isGlobalLoading}>
			{activeTab !== 'design' && (
				<TopHeader
					activeTab={activeTab}
					productId={id}
					openCommentPanel={openCommentPanel}
					setOpenCommentPanel={setOpenCommentPanel}
					loader={loader}
					setLoader={setLoader}
					stompClient={stompClient}
					onChangeActiveTab={(tab) => onChangeActiveTab(tab)}
				/>
			)}
			{activeTab === 'design' && (
				<DesignTool
					activeTab={activeTab}
					openCommentPanel={openCommentPanel}
					setOpenCommentPanel={setOpenCommentPanel}
					loader={loader}
					setLoader={setLoader}
					setIsDesignSaved={setIsDesignSaved}
					stompClient={stompClient}
					onChangeActiveTab={(tab) => onChangeActiveTab(tab)}
				/>
			)}
			{activeTab === 'materials' && (
				<MaterialsV2 productId={id} isLoading={loader} setIsLoading={setLoader} />
			)}
			{activeTab === 'measurement' && (
				<Measurement
					designToolStoreData={designToolStore}
					productId={id}
					data={howToMeasureDocResponse}
					documentGroup={'HOW_TO_MEASURE_IMAGE'}
					reference={measurementRef}
					isLoading={loader}
					setIsLoading={(val) => setLoader(val)}
				/>
			)}
			{activeTab === 'presentation' && <Presentation loader={loader} setLoader={setLoader} />}
			{activeTab === 'costing' && <CostingAgent loader={loader} setLoader={setLoader} />}
			{activeTab === 'techpack' && (
				<Techpack
					loader={loader}
					setLoader={setLoader}
					isDesignSaved={isDesignSaved}
					setIsDesignSaved={setIsDesignSaved}
				/>
			)}
			{openCommentPanel && (
				<DesignToolRightSideBar
					selectedPostId={selectedPost}
					brandName={designToolStore?.brandName}
					toolType={'design-tool'}
					callback={setOpenCommentPanel}
				/>
			)}
			{designViewInfo?.id && <DesignView />}
		</LoaderComponent>
	);
};

export default DesignEdit;
